import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Navbar, MobileNav } from "@/components/Navbar";
import { api, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { SchedulePanel } from "@/components/collab/SchedulePanel";
import { ReviewPanel } from "@/components/collab/ReviewPanel";
import { Loader2, Check, X, Send, ArrowLeft, Calendar, Gift, Tag, Banknote, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { ContentAssistant } from "@/components/ContentAssistant";

const STATUS_STYLE = {
  pending: "bg-amber-100 text-amber-700",
  accepted: "bg-emerald-100 text-emerald-700",
  declined: "bg-rose-100 text-rose-700",
  completed: "bg-zinc-200 text-zinc-700",
};

export default function CollaborationDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [collab, setCollab] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);
  const endRef = useRef(null);

  const load = useCallback(async () => {
    const [c, m] = await Promise.all([api.get(`/collaborations/${id}`), api.get(`/collaborations/${id}/messages`)]);
    setCollab(c.data);
    setMessages(m.data.messages);
  }, [id]);

  useEffect(() => { load().finally(() => setLoading(false)); }, [load]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const setStatus = async (status) => {
    setActing(true);
    try {
      const r = await api.patch(`/collaborations/${id}/status`, { status });
      setCollab(r.data);
      toast.success(`Collaboration ${status}`);
    } catch { toast.error("Action failed"); } finally { setActing(false); }
  };

  const flagCompletion = async () => {
    try { await api.post(`/collaborations/${id}/completion/flag`, { note: "" }); toast.success("Thanks — we've flagged this for review."); }
    catch { toast.error("Could not flag this"); }
  };

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    const t = text;
    setText("");
    try {
      const r = await api.post(`/collaborations/${id}/messages`, { text: t });
      setMessages((m) => [...m, r.data]);
    } catch { toast.error("Could not send"); setText(t); }
  };

  if (loading) return <div className="flex h-screen items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-emerald-600" /></div>;
  if (!collab) return <div className="p-10 text-center text-zinc-500">Collaboration not found.</div>;

  const isCreator = user?.role === "creator";
  const otherName = isCreator ? collab.business_name : collab.creator_name;
  const otherPhoto = isCreator ? collab.business_logo : collab.creator_photo;

  const detailRows = [
    { Icon: Tag, label: "Content type", value: collab.content_type || collab.platform },
    { Icon: Banknote, label: "Proposed payment", value: collab.proposed_payment != null ? `£${collab.proposed_payment}` : "—" },
    { Icon: Gift, label: "Gift offered", value: collab.gift_offered || "—" },
    { Icon: Calendar, label: "Posting date", value: collab.posting_date || "—" },
  ];

  return (
    <div className="min-h-screen bg-[#FAF9F6] nf-pb-nav">
      <Navbar />
      <div className="mx-auto max-w-4xl px-4 pt-6 pb-28 sm:px-6 sm:pb-6 lg:px-8">
        <button onClick={() => navigate("/dashboard")} className="mb-4 inline-flex items-center gap-1 text-sm text-zinc-500 hover:text-zinc-900">
          <ArrowLeft className="h-4 w-4" /> Back to dashboard
        </button>

        <div className="grid gap-6 lg:grid-cols-5">
          {/* Details */}
          <div className="lg:col-span-2 space-y-4">
            <div className="rounded-3xl border border-zinc-200 bg-white p-6">
              <div className="flex items-center gap-3">
                <div className="h-12 w-12 overflow-hidden rounded-xl bg-zinc-100">
                  {otherPhoto ? <img src={fileUrl(otherPhoto)} alt={otherName} className="h-full w-full object-cover" /> : <div className="flex h-full w-full items-center justify-center font-display font-bold text-zinc-400">{otherName?.[0]}</div>}
                </div>
                <div className="min-w-0">
                  <p className="truncate text-sm text-zinc-500">{isCreator ? "From" : "To"}</p>
                  <p className="truncate font-display font-bold text-zinc-900">{otherName}</p>
                </div>
                <span className={`ml-auto rounded-full px-2.5 py-0.5 text-xs font-semibold capitalize ${STATUS_STYLE[collab.status]}`} data-testid="collab-status">{collab.status}</span>
              </div>

              <h1 className="mt-5 font-display text-xl font-bold text-zinc-900">{collab.campaign_title}</h1>
              {collab.product_service && <p className="mt-1 text-sm text-zinc-500">Promoting: {collab.product_service}</p>}

              <div className="mt-4 space-y-2.5">
                {detailRows.map((r) => (
                  <div key={r.label} className="flex items-center gap-2 text-sm">
                    <r.Icon className="h-4 w-4 text-zinc-400" />
                    <span className="text-zinc-500">{r.label}</span>
                    <span className="ml-auto font-medium text-zinc-900">{r.value}</span>
                  </div>
                ))}
              </div>

              {collab.brief && (
                <div className="mt-4 rounded-xl bg-zinc-50 p-3">
                  <p className="text-xs font-semibold uppercase tracking-wider text-zinc-400">Brief</p>
                  <p className="mt-1 text-sm text-zinc-700">{collab.brief}</p>
                </div>
              )}

              {collab.default_content_licence && (
                <div data-testid="collab-content-licence" className="mt-4 rounded-xl border border-emerald-100 bg-emerald-50/60 p-3">
                  <p className="text-xs font-semibold uppercase tracking-wider text-emerald-700">Content sharing</p>
                  {collab.default_content_licence.applies ? (
                    <p className="mt-1 text-sm text-zinc-700">
                      The creator keeps copyright in the content. By default, {collab.business_name} may repost the agreed content on its own organic social channels (crediting the creator where practicable). Paid ads, third-party licensing or ownership transfer need separate agreement.{" "}
                      <Link to="/terms" target="_blank" className="font-semibold text-emerald-700 hover:underline">See Section 7</Link>.
                    </p>
                  ) : (
                    <p className="mt-1 text-sm text-zinc-700">
                      The default content-sharing permission doesn't apply here, so the creator keeps copyright and all usage rights. Please agree any content usage directly with each other.{" "}
                      <Link to="/terms" target="_blank" className="font-semibold text-emerald-700 hover:underline">See Section 7</Link>.
                    </p>
                  )}
                </div>
              )}

              {/* Actions */}
              {isCreator && collab.status === "pending" && (
                <div className="mt-5 grid grid-cols-2 gap-2">
                  <Button data-testid="collab-accept-btn" disabled={acting} onClick={() => setStatus("accepted")} className="rounded-full bg-emerald-600 hover:bg-emerald-700">
                    <Check className="mr-1 h-4 w-4" /> Accept
                  </Button>
                  <Button data-testid="collab-decline-btn" disabled={acting} onClick={() => setStatus("declined")} variant="outline" className="rounded-full border-rose-200 text-rose-600 hover:bg-rose-50">
                    <X className="mr-1 h-4 w-4" /> Decline
                  </Button>
                </div>
              )}
              {collab.status === "accepted" && (
                <Button data-testid="collab-complete-btn" disabled={acting} onClick={() => setStatus("completed")} className="mt-5 w-full rounded-full bg-zinc-900 hover:bg-zinc-800">
                  Mark as completed
                </Button>
              )}
              {collab.status === "completed" && (
                <button data-testid="collab-flag-btn" onClick={flagCompletion} className="mt-3 inline-flex w-full items-center justify-center gap-1 text-xs text-zinc-400 hover:text-zinc-700">
                  <AlertTriangle className="h-3 w-3" /> This wasn't completed — flag an error
                </button>
              )}
            </div>

            {(collab.status === "accepted" || collab.status === "completed") && (
              <SchedulePanel collabId={collab.id} myRole={user?.role} disabled={collab.status === "completed"} />
            )}

            {collab.status === "completed" && (
              <ReviewPanel collabId={collab.id} myRole={user?.role} otherName={otherName} />
            )}

            {isCreator && (collab.status === "accepted" || collab.status === "completed") && (
              <ContentAssistant collaborationId={collab.id} />
            )}
          </div>

          {/* Messages */}
          <div className="lg:col-span-3">
            <div className="flex h-[62vh] flex-col rounded-3xl border border-zinc-200 bg-white sm:h-[70vh]">
              <div className="border-b border-zinc-100 px-5 py-3">
                <p className="font-display font-bold text-zinc-900">Messages</p>
              </div>
              <div className="flex-1 space-y-3 overflow-y-auto p-5">
                {messages.length === 0 ? (
                  <p className="mt-10 text-center text-sm text-zinc-400">No messages yet. Say hello 👋</p>
                ) : (
                  messages.map((m) => {
                    const mine = m.sender_user_id === user.id;
                    return (
                      <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                        <div data-testid="message-bubble" className={`max-w-[80%] rounded-2xl px-4 py-2 text-sm ${mine ? "bg-emerald-600 text-white" : "bg-zinc-100 text-zinc-800"}`}>
                          {m.text}
                        </div>
                      </div>
                    );
                  })
                )}
                <div ref={endRef} />
              </div>
              <form onSubmit={send} className="flex items-center gap-2 border-t border-zinc-100 p-3">
                <Input data-testid="message-input" value={text} onChange={(e) => setText(e.target.value)} placeholder="Type a message…" className="h-11 rounded-full" />
                <Button type="submit" data-testid="message-send" size="icon" className="h-11 w-11 shrink-0 rounded-full bg-emerald-600 hover:bg-emerald-700">
                  <Send className="h-4 w-4" />
                </Button>
              </form>
            </div>
          </div>
        </div>
      </div>
      <MobileNav />
    </div>
  );
}
